// src/pages/Administracao/Feriados.js
import React, { useEffect, useMemo, useState } from "react";
import { useTable, useFilters, useSortBy } from "react-table";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";
import PageHeader from "pages/components/PageHeader";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

import {
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SyncIcon from "@mui/icons-material/Sync";
import CachedIcon from "@mui/icons-material/Cached";

const EP = {
  FER_SelMes: "Feriados/SelMes",
  FER_Ins: "Feriados/Ins",
  FER_Upd: "Feriados/Upd",
  FER_Del: "Feriados/Del",
  FER_SyncNager: "Feriados/SincronizarNager",
  FER_RenovarAnoDuplo: "Feriados/RenovarAnoCorrenteESeguinte",
};

const fmtISO = (s) => (s ? String(s).slice(0, 10) : "");

// ---------- Filtros ----------
function TextFilter({ column: { filterValue, setFilter } }) {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      placeholder="filtrar…"
      style={{
        width: "100%",
        padding: "6px 4px",
        border: "none",
        borderBottom: "1px solid rgba(0,0,0,0.2)",
        outline: "none",
        background: "transparent",
        fontSize: 12,
        color: "#556",
      }}
      onFocus={(e) => (e.target.style.borderBottom = "1px solid rgba(0,0,0,0.35)")}
      onBlur={(e) => (e.target.style.borderBottom = "1px solid rgba(0,0,0,0.2)")}
    />
  );
}

function BoolFilter({ column: { filterValue, setFilter } }) {
  return (
    <select
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      style={{
        width: "100%",
        padding: "6px 4px",
        border: "none",
        borderBottom: "1px solid rgba(0,0,0,0.2)",
        outline: "none",
        background: "transparent",
        fontSize: 12,
        color: "#556",
      }}
    >
      <option value="">Todos</option>
      <option value="1">Sim</option>
      <option value="0">Não</option>
    </select>
  );
}

// ---------- Persistência de filtros ----------
const FILTERS_KEY = "tbl:feriados:filters";
const KEEP_KEY = "tbl:feriados:keep";

export default function Feriados() {
  const { showAlert } = useAlert();
  const { temPermissao } = useAuth();

  const [ano, setAno] = useState(new Date().getFullYear());
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);

  // “Manter filtros” – por defeito TRUE
  const initialKeep =
    (() => {
      try {
        const v = localStorage.getItem(KEEP_KEY);
        return v === null ? true : JSON.parse(v);
      } catch {
        return true;
      }
    })() ?? true;
  const [keepFilters, setKeepFilters] = useState(initialKeep);

  const initialFilters = keepFilters
    ? (() => {
        try {
          return JSON.parse(localStorage.getItem(FILTERS_KEY) || "[]");
        } catch {
          return [];
        }
      })()
    : [];

  // dialog inserir/editar
  const [dlg, setDlg] = useState({
    open: false,
    edit: false,
    reg: {
      id: 0,
      data: "",
      nome: "",
      alcancetipo: "municipal", // nacional | municipal | empresa
      alcancetod: "",
      fonte: "MANUAL",
      oficial: true,
      inativo: 0,
      renovar: 0,
    },
  });

  // dialog de sincronização Nager
  const defaultSyncYear = new Date().getFullYear() + 1;
  const [syncDlg, setSyncDlg] = useState({
    open: false,
    ano: defaultSyncYear,
    pais: "PT",
    loading: false,
  });

  const podeW = temPermissao("Menu Administração", "w");
  const podeU = temPermissao("Menu Administração", "u");
  const podeD = temPermissao("Menu Administração", "d");

  // carregar lista (ano → 12x SelMes)
  async function carregar() {
    setLoading(true);
    try {
      const calls = Array.from({ length: 12 }, (_, i) =>
        handleApi(api.post(EP.FER_SelMes, { ano, mes: i + 1 }))
      );
      const results = await Promise.all(calls);
      const all = results.flatMap(([data]) => (Array.isArray(data) ? data : [])) || [];

      // normaliza datas (YYYY-MM-DD)
      const norm = all.map((r) => ({ ...r, data: fmtISO(r.data) }));
      // ordena
      norm.sort((a, b) => (a.data > b.data ? 1 : -1));

      setDados(norm);
    } catch {
      showAlert("Falha ao carregar feriados.", "error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ano]);

  // ---------- Tabela ----------
  const columns = useMemo(
    () => [
      {
        Header: "",
        id: "acoes",
        disableFilters: true,
        width: 72,
        minWidth: 56,
        maxWidth: 90,
        Cell: ({ row }) => {
          const a = row.original;
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <IconButton
                color="primary"
                size="small"
                disabled={!podeU}
                onClick={() => abrirEditar(a)}
                title="Editar"
              >
                <EditIcon fontSize="small" />
              </IconButton>

              <IconButton
                color="error"
                size="small"
                disabled={!podeD}
                onClick={() => apagar(a)}
                title="Apagar"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </div>
          );
        },
      },
      { Header: "Data", accessor: (r) => fmtISO(r.data), id: "data", Filter: TextFilter },
      { Header: "Nome", accessor: "nome", Filter: TextFilter },
      { Header: "Âmbito", accessor: "alcancetipo", Filter: TextFilter },
      { Header: "Alcance/Região", accessor: "alcancetod", Filter: TextFilter },
      { Header: "Fonte", accessor: "fonte", Filter: TextFilter },
      {
        Header: "Oficial",
        accessor: (r) => (r.oficial ? "1" : "0"),
        id: "oficial",
        Cell: ({ row }) => (row.original.oficial ? "Sim" : "Não"),
        Filter: BoolFilter,
      },
      {
        Header: "Renovar",
        accessor: (r) => String(r.renovar ?? 0),
        id: "renovar",
        Cell: ({ row }) => ((row.original.renovar ?? 0) === 1 ? "Sim" : "Não"),
        Filter: BoolFilter,
      },
      {
        Header: "Inativo",
        accessor: (r) => String(r.inativo ?? 0),
        id: "inativo",
        Cell: ({ row }) => ((row.original.inativo ?? 0) === 1 ? "Sim" : "Não"),
        Filter: BoolFilter,
      },
    ],
    [podeU, podeD]
  );

  const table = useTable(
    {
      columns,
      data: dados,
      initialState: { filters: initialFilters },
      autoResetFilters: false,
      autoResetSortBy: false,
    },
    useFilters,
    useSortBy
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, setAllFilters, state } =
    table;

  // regras de persistência dos filtros
  useEffect(() => {
    if (!keepFilters && (state.filters?.length || 0) > 0) {
      setAllFilters([]);
      try {
        localStorage.removeItem(FILTERS_KEY);
      } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dados]);

  useEffect(() => {
    try {
      localStorage.setItem(KEEP_KEY, JSON.stringify(keepFilters));
    } catch {}
    if (!keepFilters) {
      setAllFilters([]);
      try {
        localStorage.removeItem(FILTERS_KEY);
      } catch {}
    }
  }, [keepFilters, setAllFilters]);

  useEffect(() => {
    if (!keepFilters) return;
    try {
      localStorage.setItem(FILTERS_KEY, JSON.stringify(state.filters || []));
    } catch {}
  }, [state.filters, keepFilters]);

  // ---------- Ações ----------
  const abrirNovo = () =>
    setDlg({
      open: true,
      edit: false,
      reg: {
        id: 0,
        data: "",
        nome: "",
        alcancetipo: "municipal",
        alcancetod: "",
        fonte: "MANUAL",
        oficial: true,
        inativo: 0,
        renovar: 0,
      },
    });

  const abrirEditar = (r) =>
    setDlg({
      open: true,
      edit: true,
      reg: {
        id: r.id,
        data: r.data?.substring(0, 10) || "",
        nome: r.nome || "",
        alcancetipo: r.alcancetipo || "municipal",
        alcancetod: r.alcancetod || "",
        fonte: r.fonte || "MANUAL",
        oficial: !!r.oficial,
        inativo: r.inativo ?? 0,
        renovar: r.renovar ?? 0,
      },
    });

  const fecharDlg = () => setDlg((d) => ({ ...d, open: false }));

  const gravar = async () => {
    const b = dlg.reg;
    if (!b.data || !b.nome) {
      showAlert("Preenche Data e Nome.", "warning");
      return;
    }
    const body = {
      ...b,
      oficial: !!b.oficial,
      inativo: Number(b.inativo || 0),
      renovar: Number(b.renovar || 0),
    };
    const [_, err, meta] = await handleApi(api.post(dlg.edit ? EP.FER_Upd : EP.FER_Ins, body));
    if (err) {
      showAlert(err, "error");
      return;
    }
    if (meta?.message) showAlert(meta.message, meta.severity || "success");
    fecharDlg();
    await carregar();
  };

  const apagar = async (r) => {
    if (!window.confirm(`Apagar "${r.nome}" em ${r.data}?`)) return;
    const [_, err, meta] = await handleApi(api.post(EP.FER_Del, { id: r.id }));
    if (err) {
      showAlert(err, "error");
      return;
    }
    if (meta?.message) showAlert(meta.message, meta.severity || "success");
    await carregar();
  };

  const abrirSync = () => setSyncDlg((s) => ({ ...s, open: true }));

  const confirmarSync = async () => {
    setSyncDlg((s) => ({ ...s, loading: true }));
    const { ano: anoSync, pais } = syncDlg;
    const [_, err, meta] = await handleApi(api.post(EP.FER_SyncNager, { ano: anoSync, pais }));
    setSyncDlg((s) => ({ ...s, loading: false }));
    if (err) {
      showAlert(err, "error");
      return;
    }
    showAlert(meta?.message || "Sincronizado.", meta?.severity || "success");
    setSyncDlg((s) => ({ ...s, open: false }));
    await carregar();
  };

  const renovarAnoDuplo = async () => {
    const payload = { anoBase: ano, dryRun: false };
    const [_, err, meta] = await handleApi(api.post(EP.FER_RenovarAnoDuplo, payload));
    if (err) {
      showAlert(err, "error");
      return;
    }
    showAlert(
      meta?.message || `Renovação concluída para ${ano} e ${ano + 1}.`,
      meta?.severity || "success"
    );
    await carregar();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <PageHeader title="Feriados" subtitle="Gestão e sincronização" />

      <MDBox pt={1}>
        <Card>
          <MDBox p={3}>
            <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={2} gap={2}>
              <MDBox display="flex" alignItems="center" gap={1}>
                <MDTypography variant="h6">Feriados {ano}</MDTypography>
              </MDBox>

              <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={keepFilters}
                      onChange={(e) => setKeepFilters(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Manter filtros"
                />

                <MDInput
                  type="number"
                  label="Ano"
                  value={ano}
                  onChange={(e) => setAno(parseInt(e.target.value || new Date().getFullYear(), 10))}
                  style={{ width: 120 }}
                />

                <MDButton
                  onClick={abrirSync}
                  disabled={!podeW}
                  startIcon={<SyncIcon />}
                  color="info"
                >
                  Sincronizar Nager
                </MDButton>

                <MDButton
                  onClick={renovarAnoDuplo}
                  disabled={!podeW}
                  startIcon={<CachedIcon />}
                  color="secondary"
                >
                  Renovar ano e seguinte
                </MDButton>

                <MDButton color="success" onClick={abrirNovo} disabled={!podeW}>
                  Novo Feriado
                </MDButton>
              </MDBox>
            </MDBox>

            <MDBox sx={{ height: "68vh", overflow: "auto", borderRadius: 1 }}>
              <table {...getTableProps()} style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  {headerGroups.map((hg) => (
                    <tr {...hg.getHeaderGroupProps()}>
                      {hg.headers.map((col) => (
                        <th
                          {...col.getHeaderProps(col.getSortByToggleProps())}
                          style={{
                            width: col.id === "acoes" ? 72 : undefined,
                            position: "sticky",
                            top: 0,
                            zIndex: 2,
                            background: "#fff",
                            textAlign: "left",
                            padding: "6px 8px",
                            fontSize: 12,
                            color: "#7b809a",
                            verticalAlign: "bottom",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center" }}>
                            {col.render("Header")}
                            {col.isSorted ? (
                              col.isSortedDesc ? (
                                <span style={{ marginLeft: 6 }}>▼</span>
                              ) : (
                                <span style={{ marginLeft: 6 }}>▲</span>
                              )
                            ) : null}
                          </div>
                          {!col.disableFilters && (
                            <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 4 }}>
                              {col.canFilter ? col.render("Filter") : null}
                            </div>
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {rows.map((row) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()} style={{ borderBottom: "1px solid #f0f2f5" }}>
                        {row.cells.map((cell) => (
                          <td
                            {...cell.getCellProps()}
                            style={{
                              width: cell.column.id === "acoes" ? 72 : undefined,
                              whiteSpace: "nowrap",
                              padding: "10px 8px",
                              fontSize: 12,
                              color: "#344767",
                            }}
                          >
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>

      {/* Dialog Inserir/Editar */}
      <Dialog open={dlg.open} onClose={fecharDlg} fullWidth maxWidth="sm">
        <DialogTitle>{dlg.edit ? "Editar feriado" : "Novo feriado"}</DialogTitle>
        <DialogContent dividers>
          <MDBox display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            <MDBox>
              <label>Data</label>
              <TextField
                type="date"
                fullWidth
                value={dlg.reg.data}
                onChange={(e) => setDlg((d) => ({ ...d, reg: { ...d.reg, data: e.target.value } }))}
              />
            </MDBox>

            <MDBox>
              <label>Âmbito</label>
              <select
                value={dlg.reg.alcancetipo}
                onChange={(e) =>
                  setDlg((d) => ({ ...d, reg: { ...d.reg, alcancetipo: e.target.value } }))
                }
                style={{ width: "100%", padding: 12, borderRadius: 6, border: "1px solid #ced4da" }}
              >
                <option value="municipal">Municipal</option>
                <option value="nacional">Nacional</option>
                <option value="empresa">Empresa</option>
              </select>
            </MDBox>

            <MDBox gridColumn="1 / span 2">
              <label>Nome</label>
              <TextField
                fullWidth
                value={dlg.reg.nome}
                onChange={(e) => setDlg((d) => ({ ...d, reg: { ...d.reg, nome: e.target.value } }))}
              />
            </MDBox>

            <MDBox gridColumn="1 / span 2">
              <label>Alcance/Região (p/ municipal)</label>
              <TextField
                fullWidth
                value={dlg.reg.alcancetod}
                onChange={(e) =>
                  setDlg((d) => ({ ...d, reg: { ...d.reg, alcancetod: e.target.value } }))
                }
              />
            </MDBox>

            <MDBox>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!dlg.reg.oficial}
                    onChange={(e) =>
                      setDlg((d) => ({ ...d, reg: { ...d.reg, oficial: e.target.checked } }))
                    }
                  />
                }
                label="Oficial"
              />
            </MDBox>

            <MDBox>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Number(dlg.reg.renovar) === 1}
                    onChange={(e) =>
                      setDlg((d) => ({
                        ...d,
                        reg: { ...d.reg, renovar: e.target.checked ? 1 : 0 },
                      }))
                    }
                  />
                }
                label="Renovar anualmente"
              />
            </MDBox>

            <MDBox>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Number(dlg.reg.inativo) === 1}
                    onChange={(e) =>
                      setDlg((d) => ({
                        ...d,
                        reg: { ...d.reg, inativo: e.target.checked ? 1 : 0 },
                      }))
                    }
                  />
                }
                label="Inativo"
              />
            </MDBox>

            <MDBox>
              <label>Fonte</label>
              <TextField
                fullWidth
                value={dlg.reg.fonte}
                onChange={(e) =>
                  setDlg((d) => ({ ...d, reg: { ...d.reg, fonte: e.target.value } }))
                }
              />
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={fecharDlg} color="secondary">
            Fechar
          </MDButton>
          <MDButton onClick={gravar} color="success" disabled={!podeW && !dlg.edit}>
            {dlg.edit ? "Gravar" : "Inserir"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Dialog Sincronizar Nager */}
      <Dialog
        open={syncDlg.open}
        onClose={() => setSyncDlg((s) => ({ ...s, open: false }))}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Sincronizar Nager</DialogTitle>
        <DialogContent dividers>
          <MDBox display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            <MDBox>
              <label>Ano</label>
              <TextField
                type="number"
                fullWidth
                value={syncDlg.ano}
                onChange={(e) =>
                  setSyncDlg((s) => ({
                    ...s,
                    ano: parseInt(e.target.value || defaultSyncYear, 10),
                  }))
                }
              />
            </MDBox>
            <MDBox>
              <label>País</label>
              <TextField
                fullWidth
                value={syncDlg.pais}
                onChange={(e) =>
                  setSyncDlg((s) => ({ ...s, pais: (e.target.value || "PT").toUpperCase() }))
                }
              />
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton color="secondary" onClick={() => setSyncDlg((s) => ({ ...s, open: false }))}>
            Cancelar
          </MDButton>
          <MDButton color="info" onClick={confirmarSync} disabled={syncDlg.loading}>
            {syncDlg.loading ? "A sincronizar…" : "Sincronizar"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
